import { IAPIResponse } from "./api-response";
import { AbortError } from "./errors/abort-error";
import { ArgumentError } from "./errors/argument-error";
import { NetworkError } from "./errors/network-error";
import authHeader from "./auth-header";

const ERR_MSG_503 = "A szolgáltatás nem elérhető, kérjük, próbálja újra egy kis idő múlva.";

/**
 * A HTTP client utility class to communicate with the AudioNodes WebApi on the backend with user authentication.
 */
export class HttpClient
{
    private _apiEndpoint: string;

    /**
     * @param apiEndpoint An absolute URL pointing to the WebApi root, may optionally include a trailing slash.
     * @param accessTokenProvider
     */
    constructor(apiEndpoint: string)
    {
        if (apiEndpoint.endsWith("/"))
        {
            apiEndpoint = apiEndpoint.substring(0, apiEndpoint.length - 1);
        }

        if (!apiEndpoint.startsWith("http://") && !apiEndpoint.startsWith("https://"))
        {
            throw new ArgumentError("Must be a valid absolute URL.", "apiEndpoint", apiEndpoint);
        }

        this._apiEndpoint = apiEndpoint;
    }

    /**
     * Performs a HTTP GET request.
     * @param url The request URL, relative to the API endpoint.
     * @param allowAnonymous Whether to bypass user authentication and allow anonymous (guest) user requests. [Default = false]
     * @param responseType Default value is "text".
     */
    public async get<T = any>(url: string, accessToken: string, timeout?: number, responseType?: "blob" | undefined)
    {
        return this.sendRequest<T>("GET", url, accessToken, undefined, timeout, responseType);
    }

    /**
     * Performs a HTTP POST request.
     * @param url The request URL, relative to the API endpoint.
     * @param allowAnonymous Whether to bypass user authentication and allow anonymous (guest) user requests. [Default = false]
     * @param data The data to include in the body of the POST request; can be a FormData object, a form-data string, or an Object as JSON.
     * @param responseType Default value is "text".
     */
    public async post<T = any>(url: string, accessToken: string, data?: FormData | string | Object, timeout?: number, responseType?: "blob" | undefined)
    {
        return this.sendRequest<T>("POST", url, accessToken, data, timeout, responseType);
    } /**
     * Performs a HTTP DELETE request.
     * @param url The request URL, relative to the API endpoint.
     * @param allowAnonymous Whether to bypass user authentication and allow anonymous (guest) user requests. [Default = false]
     * @param data The data to include in the body of the POST request; can be a FormData object, a form-data string, or an Object as JSON.
     * @param responseType Default value is "text".
     */
    public async delete<T = any>(url: string, accessToken: string, data?: FormData | string | Object, timeout?: number, responseType?: "blob" | undefined)
    {
        return this.sendRequest<T>("DELETE", url, accessToken, data, timeout, responseType);
    }

    public async sendRequest<T = any>(
        requestType: "GET" | "POST" | "PATCH" | "DELETE",
        url: string,
        accessToken: string,
        formData?: FormData | string | Object,
        timeout?: number,
        responseType?: "blob" | undefined
    ): Promise<IAPIResponse<T> | any>
    {
        let response = await new Promise<IAPIResponse | any>((resolve, reject) =>
        {
            let xhr = new XMLHttpRequest();
            let token = authHeader();

            xhr.open(requestType, `${this._apiEndpoint}/${url}`, true);

            if (timeout)
            {
                xhr.timeout = timeout;
            }

            if (responseType)
            {
                xhr.responseType = responseType;
            }

            if (token && token.length > 0)
            {
                xhr.setRequestHeader("Authorization", "Bearer " + token);
            }

            // Default response type is text.
            if (!responseType)
            {
                xhr.onload = async () =>
                {
                    // The response is supposed to be JSON.
                    // First try to parse it like so, but also provide fallback in case of non-success HTTP responses.
                    let responseText: string = xhr.responseText;

                    let responseJson: IAPIResponse;

                    try
                    {
                        responseJson = JSON.parse(responseText) as IAPIResponse;
                    }
                    catch (err: any)
                    {
                        // Couldn't parse response as JSON.
                        // However, as a fallback, check whether it appears to be an error of some sort (gateway, dns, whatever).
                        // If so, reject promise with response as raw text.

                        if (xhr.status === 503 || xhr.responseText?.startsWith("The service is unavailable"))
                        {
                            // Special-case handling for backend update deploys.
                            // While the backend app is being replaced for an update, it responds with 503 status code.
                            // However, the response body itself is not JSON, and thus JSON.parse will throw, which is handled here.
                            reject(new Error(ERR_MSG_503));
                        }
                        else if (xhr.status >= 400)
                        {
                            // Some other error.
                            reject(new Error(this._stripHtml(responseText)));
                        }
                        else
                        {
                            // Non-error response based on HTTP status code, but we can't parse it.
                            // This shouldn't happen in normal circumstances.
                            reject(new Error("Failed to process response: " + err.message));
                        }

                        return;
                    }

                    // Got a standard JSON response from the API.
                    if (responseJson.success)
                    {
                        resolve(responseJson);
                    }
                    else
                    {
                        reject(new Error(responseJson.error || "Ismeretlen eredetű szerverhiba."));
                    }

                    resolve(responseJson);
                };
            }
            else // Handling blobs
            {
                xhr.onload = async () =>
                {
                    // The response is supposed to be JSON.
                    // First try to parse it like so, but also provide fallback in case of non-success HTTP responses.
                    let responseText: string = xhr.response;

                    if (xhr.status === 200)
                    {
                        let data = {
                            header: xhr.getResponseHeader("Content-Disposition")?.split("filename=")[1].toString(),
                            response: responseText
                        };
                        resolve(data);
                    }

                    // Something happened during file download.
                    // However, as a fallback, check whether it appears to be an error of some sort (gateway, dns, whatever).
                    // If so, reject promise with response as raw text.

                    if (xhr.status === 503 || xhr.responseText?.startsWith("The service is unavailable"))
                    {
                        // Special-case handling for backend update deploys.
                        // While the backend app is being replaced for an update, it responds with 503 status code.
                        // However, the response body itself is not JSON, and thus JSON.parse will throw, which is handled here.
                        reject(new Error(ERR_MSG_503));
                    }
                    else if (xhr.status >= 400)
                    {
                        // Some other error.
                        reject(new Error(this._stripHtml(responseText)));
                    }
                    else
                    {
                        // Non-error response based on HTTP status code, but we can't parse it.
                        // This shouldn't happen in normal circumstances.
                        reject(new Error("Failed to process response: " + xhr.responseText));
                    }
                };
            }

            if (typeof formData === "string")
            {
                // Assume JSON.
                xhr.setRequestHeader("Content-Type", "application/json");
                xhr.send(formData);
            }
            else if (formData instanceof FormData)
            {
                xhr.send(formData);
            }
            else if (formData)
            {
                xhr.setRequestHeader("Content-Type", "application/json");
                xhr.send(JSON.stringify(formData));
            }
            else
            {
                xhr.send();
            }

            xhr.ontimeout = () => reject(new NetworkError("A kérés túllépte a tervezett időt, kérjük, próbálja újra.", xhr.status));
            xhr.onabort = () => reject(new AbortError());
            xhr.onerror = () =>
            {
                switch (xhr.status)
                {
                    case 503:
                        reject(new NetworkError(ERR_MSG_503, xhr.status));
                        break;

                    default:
                        reject(new NetworkError("Nem várt hiba történt.", xhr.status));
                        break;
                }
            };
        });

        return response;
    }

    private _stripHtml(html: string): string
    {
        // Create a <div> and use DOM APIs to strip HTML.
        let div = document.createElement("DIV");
        div.innerHTML = html;
        return div.textContent || div.innerText || "";
    }
}
